import * as yup from 'yup'
import {
  compose,
  map,
  prop,
  uniq,
  split,
  path as rPath,
  has,
  slice,
  ifElse,
  isNil,
} from 'ramda'

import { isNumber } from 'utils/types'

const formatPath = compose(
  map((item) => {
    const number = parseFloat(item)

    // eslint-disable-next-line no-restricted-globals
    return !isNaN(number) ? number : item
  }),
  split(/].|[[\].]/), // ]. or [ or ] or .
  prop('path')
)

export const errorPaths = compose(map(formatPath), prop('inner'))

export const schemaProp = (schema) => (theSchemaProp, context) => {
  const extendReach = yup.reach(schema, theSchemaProp).resolve({ context })
  extendReach.context = context
  return extendReach
}

// useful for one-off schema
export const extendSchema = (schema, context) => {
  const extended = schema.resolve({ context })
  extended.context = context
  return extended
}

export const getErrorParentIds = (err) => {
  const paths = errorPaths(err)

  return uniq(
    paths.reduce((acc, errorPath) => {
      // feature
      if (errorPath.length === 1) {
        return [...acc, prop('id', err.value)]
      }

      const parentPath = slice(0, -1, errorPath)
      const parent = rPath(parentPath, err.value)

      const isOutput = has('type')
      const parentId = ifElse(
        isOutput,
        prop('id'), // output
        prop('uri') // query/function
      )

      return [...acc, parentId(parent)]
    }, [])
  )
}

// configuration for yup
yup.setLocale({
  mixed: {
    required: 'Required',
  },
  string: {
    length: 'must be ${length} chars', // eslint-disable-line no-template-curly-in-string
  },
})

// if defined, then value must be of exact length
yup.addMethod(
  yup.string,
  'optionalLength',
  function optionalLength(length, message) {
    return this.test({
      name: 'length',
      message: message || 'must be ${length} chars', // eslint-disable-line no-template-curly-in-string
      params: { length },
      test(value) {
        return !value || value.length === length
      },
    })
  }
)

/**
 * Determines if a value is less than the context prop passed in
 * What makes this different than 'lessThan' is that it works for both context of an object AND
 * works for local context passed into a field component
 *
 * Must use 'test' instead of 'when' because, with 'when' we get a circular dependency error from yup
 *
 * @param {string} contextProp
 * @param {string} message
 *
 * @example
 *   yup.string().required().lessThanContext('valueMax')
 *
 *   // => will immmediately work for validation of the object
 *   // => need to pass valueMax to your rule (passed into your field component) -> rule('valueMin', { valueMax })
 */
yup.addMethod(
  yup.mixed,
  'lessThanContext',
  function lessThanContext(contextProp, message) {
    return this.test({
      name: 'lessThanContext',
      test(value) {
        const {
          parent,
          path,
          options: { context = {} },
        } = this

        const lessThanTest = (comparator) => {
          const comparatorNotSet = isNil(comparator)
          return (
            comparatorNotSet ||
            value <= comparator ||
            this.createError({
              path,
              message: message || `must be less than or equal to ${comparator}`,
            })
          )
        }

        if (!isNumber(value)) return true

        if (has(contextProp, context)) return lessThanTest(context[contextProp])

        return lessThanTest(parent[contextProp])
      },
    })
  }
)

/**
 * Determines if a value is more than the context prop passed in
 * What makes this different than 'moreThan' is that it works for both context of an object AND
 * works for local context passed into a field component
 *
 * Must use 'test' instead of 'when' because, with 'when' we get a circular dependency error from yup
 *
 * @param {string} contextProp
 * @param {string} message // optional
 *
 * @example
 *   yup.string().required().moreThanContext('valueMin')
 *
 *   // => will immmediately work for validation of the object
 *   // => need to pass valueMin to your rule (passed into your field component) -> rule('valueMax', { valueMin })
 */
yup.addMethod(
  yup.mixed,
  'moreThanContext',
  function moreThanContext(contextProp, message) {
    return this.test({
      name: 'greaterThanContext',
      test(value) {
        const {
          parent,
          path,
          options: { context = {} },
        } = this

        const moreThanTest = (comparator) => {
          const comparatorNotSet = isNil(comparator)
          return (
            comparatorNotSet ||
            value >= comparator ||
            this.createError({
              path,
              message:
                message || `must be greater than or equal to ${comparator}`,
            })
          )
        }

        if (!isNumber(value)) return true

        if (has(contextProp, context)) return moreThanTest(context[contextProp])

        return moreThanTest(parent[contextProp])
      },
    })
  }
)

/**
 * Marks field required only if a condition is met
 *
 * NOTE: this will always make the field (in your component) required
 * This ensures the object is not validated when certain conditions are me
 *
 * If you are trying to make a field conditionally required there are other ways to do that (see subGroup in featureMetaSchema)
 *
 * @param {string} contextIfApplicable
 * @param {fn} fn
 * @param {string} message // optional
 *
 * @example
 *   yup.string().requiredIfContext('value', v => v < 3)
 *   // => will enforce required field if the 'value' field (external from this rule) is less than 3
 */
yup.addMethod(
  yup.mixed,
  'requiredIfContext',
  function requiredIfContext(contextIfApplicable, fn, message) {
    return this.test({
      name: 'required',
      test(value) {
        const { parent, path } = this

        const testValue = () =>
          !isNil(value) ||
          this.createError({ path, message: message || 'Required' })

        // if not parent, then we know we are at the component level
        if (!parent) {
          return testValue()
        }

        const parentProp = parent[contextIfApplicable]
        const shouldTestValue = parentProp && fn(parentProp)

        // if not testing the value then return true (valid)
        return !shouldTestValue || testValue()
      },
    })
  }
)

/**
 * @param {string} contextProp
 * @param {string} message // optional
 *
 * @example
 *    yup.string().includedIn('definedValues')
 *    // => will check if the value is included in the 'definedVAlues' array (in schema, or in context)
 */
yup.addMethod(
  yup.mixed,
  'includedIn',
  function includedIn(contextProp, message) {
    return this.test({
      name: 'available',
      test(value) {
        const {
          parent,
          path,
          options: { context },
        } = this

        const includedInTest = (comparator) => {
          if (isNil(value)) return true
          return (
            comparator.includes(value) ||
            this.createError({
              path,
              message: message || 'The value you entered is unavailable', // TODO: string
            })
          )
        }

        if (context[contextProp]) return includedInTest(context[contextProp])

        return includedInTest(parent[contextProp])
      },
    })
  }
)

/**
 * @param {string} contextProp
 * @param {string} message // optional
 *
 * @example
 *    yup.string().hasIn('definedValues')
 *    // => will check if the value is included in the 'definedValues' set (in schema, or in context)
 */
yup.addMethod(yup.mixed, 'hasIn', function includedIn(contextProp, message) {
  return this.test({
    name: 'has',
    test(value) {
      const {
        parent,
        path,
        options: { context },
      } = this

      const hasIn = (comparator) => {
        if (!value) return true

        return (
          comparator.has(value) ||
          this.createError({
            path,
            message: message || 'must be defined',
          })
        )
      }

      if (context[contextProp]) return hasIn(context[contextProp])

      return hasIn(parent[contextProp])
    },
  })
})

// must export modified version of yup
export { yup }
